<template>
  <section>
    <!-- SB-18 -->
    <div
      id="sb-18"
      v-if="$store.state.currentLocation >= 18"
    >
      <banner-full-width headerText="Environment and weather"></banner-full-width>
      <content-block>
        <!-- 5-01-00 -->
        <!-- <banner-full-width headerText=""></banner-full-width> -->
        <!-- <h1 class="animated infinite bounce delay-2s">Example</h1> -->
        <h2 class="content-block__header content-block__header--red">Up next</h2>
        <p>The weather may change drastically in a matter of minutes while you’re rowing.</p>
        <p>This section will prepare you to examine your rowing environment and to predict, plan for and respond to weather and sudden changes in conditions. </p>
        <button-sequenced
          class="btn-nav--red"
          :loc="19"
          link="sb-19"
        >
          Tell me more
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-19 -->
    <div
      id="sb-19"
      v-if="$store.state.currentLocation >= 19"
    >
      <content-block>
        <h2 class="content-block__header content-block__header--red">Read and interpret the weather</h2>
        <p>Dangerous weather seldom appears without warning, as the conditions that result in storms, high winds, or lightning can be detected by weather radar, satellites and infrared imaging as they approach. Sudden temperature drops may bring dense fog or signal the arrival of a weather change.</p>
        <p class="pb-5">Some clubs have weather stations displaying current local conditions. Use this information along with weather details available from Environment Canada and various weather sites or apps, including:</p>
        <div class="row">
          <div class="col-md-2 col-sm-6">
            <img
              src="@/assets/img/_content/19-myradar.png"
              class="img-fluid mx-auto d-block  mb-4"
              alt="1"
              srcset=""
            >
            <p class="text-red text-center">MyRadar</p>
          </div>
          <div class="col-md-2 col-sm-6">
            <img
              src="@/assets/img/_content/19-accuwether.png"
              class="img-fluid mx-auto d-block  mb-4"
              alt="1"
              srcset=""
            >
            <p class="text-red text-center">AccuWeather</p>
          </div>
          <div class="col-md-2 col-sm-6">
            <img
              src="@/assets/img/_content/19-predictwind.png"
              class="img-fluid mx-auto d-block  mb-4"
              alt="1"
              srcset=""
            >
            <p class="text-red text-center">PredictWind</p>
          </div>
          <div class="col-md-2 col-sm-6">
            <img
              src="@/assets/img/_content/19-sailflow.png"
              class="img-fluid mx-auto d-block  mb-4"
              alt="1"
              srcset=""
            >
            <p class="text-red text-center">SailFlow</p>
          </div>
          <div class="col-md-2 col-sm-6">
            <img
              src="@/assets/img/_content/19-windy.png"
              class="img-fluid mx-auto d-block  mb-4"
              alt="1"
              srcset=""
            >
            <p class="text-red text-center">Windy</p>
          </div>
          <div class="col-md-2 col-sm-6">
            <img
              src="@/assets/img/_content/19-weathercan.png"
              class="img-fluid mx-auto d-block  mb-4"
              alt="1"
              srcset=""
            >
            <p class="text-red text-center">WeatherCAN</p>
          </div>
        </div>
        <p class="pt-2">It is important to be familiar with how weather conditions change locally, so you can read and interpret the information available on these sites and apps. </p>
        <p>Strong winds, whitecaps, thunder, lightning and fog are all indications that you should not go on the water. </p>
        <p>If these conditions arise once you are on the water, return to the boathouse or get to the nearest safe haven.</p>
        <button-sequenced
          class="btn-nav--red"
          :loc="20"
          link="sb-20"
        >
          I’ll mind the weather
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-20 -->
    <div
      id="sb-20"
      v-if="$store.state.currentLocation >= 20"
    >
      <content-block>
        <h2 class="content-block__header content-block__header--red">Identify safe havens before you row</h2>
        <p>Safe havens are places where you can safely get ashore if weather or water conditions become too dangerous for you to get back to the club. It is important to <strong class="text-red">know where safe havens are before you go on the water</strong>.</p>
        <p>Safe havens should be marked on the local waters and traffic pattern maps posted around your club and docks. Check out these areas regularly so you know where to go in dangerous conditions.</p>
        <p>If you need a safe haven but don’t know where to find one, look for places that you can get close to without crashing into rocks, trees, walls or other physical installations, or where you can stand in the water to be able to climb ashore and get your shell out of the water.</p>
        <p>Being aware of safe havens enables you to give directions to your coach or to rescue crews if needed.</p>
        <button-sequenced
          class="btn-nav--red"
          :loc="21"
          link="sb-21"
        >
          That’s crucial
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-21 -->
    <div
      id="sb-21"
      v-if="$store.state.currentLocation >= 21"
    >
      <content-block>
        <h2 class="content-block__header content-block__header--red">Follow RCA weather protocols</h2>
        <p>RCA has several documents that discuss weather and safety protocols for rowing. </p>
        <ul class="chevron-list">
          <li><em>RCA Weather Protocol</em></li>
          <li><em>Setting a Straight Course – RCA Weather Protocol</em></li>
          <li><em>Rowing Safely: RCA Safety Requirements & Safety Guidelines</em></li>
        </ul>
        <p>These are all available for you to consult on the RCA website.</p>
        <button-sequenced
          class="btn-nav--red"
          :loc="22"
          link="sb-22"
        >
          I’ll review them
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-22 -->
    <div
      id="sb-22"
      class="bg-img bg-img--03"
      v-if="$store.state.currentLocation >= 22"
    >
      <content-block>
        <h2 class="content-block__header content-block__header--red">Assess extreme conditions</h2>
        <p>It is paramount to know what the weather is expected to do in the time that you are planning to be on the water <strong class="text-red">and for a couple of hours after you plan to be off the water</strong>. This prepares you to respond quickly if you observe rapid development of dangerous conditions.</p>
        <p><strong class="text-red">You are also responsible for the safety of those who may have to rescue you</strong> if you have not paid attention to conditions and are caught in an emergency.</p>
        <p>If you hear thunder, make your way as quickly as possible back to the docks. If you have waited too long and the storm is on you, go directly to shore and get off the water as quickly as possible. Notify your club manager or coach if you put ashore somewhere other than your club docks.</p>
        <p>If wind and waves are too strong for you to make it safely back to the dock, make your way to the nearest safe haven or landing place.</p>
        <div class="row">
          <div class="col-md-2">
            <img
              src="@/assets/img/_content/22-fog.svg"
              class="img-fluid mb-4"
              alt="1"
              srcset=""
            >
          </div>
          <div class="col-md-10">
            <p><strong class="text-red">A note on fog</strong></p>
            <p>Dense fog is a challenge in many parts of Canada and may roll in unexpectedly and quickly. </p>
            <p>When you see fog roll in, assess whether you can make it back to the dock before visibility is reduced and you are unable to see the shore or the dock you are heading to. </p>
            <p>If you cannot see, head directly to the closest shore to avoid getting lost or being hit by other vessels that can’t see you.</p>
          </div>
        </div>
        <button-sequenced
          class="btn-nav--red"
          :loc="23"
          link="sb-23"
        >
          Safe call
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-23 -->
    <div
      id="sb-23"
      v-if="$store.state.currentLocation >= 23"
    >
      <content-block>
        <h2 class="content-block__header content-block__header--red">Prepare for cold water rowing</h2>
        <p>If you are rowing in a cold-water environment:</p>
        <ul class="chevron-list">
          <li>Wear multiple layers to keep warmth in. </li>
          <li>Select protective clothing materials that allow you to stay dry and insulated against heat loss but with flexibility for rowing motion and activity.</li>
          <li>Be aware of your club protocol for water temperature of 15°C or below, or other adverse weather conditions.</li>
        </ul>
        <p>Cold water protocols are an important part of your risk management plan. <strong class="text-red">Submersion in cold water is extremely dangerous</strong>, causing a person to lose heat far more rapidly than exposure to cold air. Depending on how cold the water is, loss of muscle function and mental confusion can occur within minutes. </p>
        <h3 class="text-red mt-4">Immersion in water </h3>
        <div class="row">
          <div class="col-md-6">
            <p>If you find yourself in cold water, get on top of the rowing shell with as much of your core body out of the water as possible.</p>
            <p>Adopt the Heat Escape Lessening Position (HELP) position if you are by yourself or the Huddle position if you’re in a group, to reduce exposure of the body core area to cold water and reduce the effects of hypothermia.</p>
          </div>
          <div class="col-md-6">
            <img
              src="@/assets/img/_content/23-help-huddle--swap.png"
              class="img-fluid mb-4"
              alt="1"
              srcset=""
            >
          </div>
        </div>
        <button-sequenced
          class="btn-nav--red"
          :loc="24"
          link="sb-24"
        >
          I’ll prepare for cold weather
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-24 -->
    <div
      id="sb-24"
      class="bg-img bg-img--04"
      v-if="$store.state.currentLocation >= 24"
    >
      <content-block>
        <h2 class="content-block__header content-block__header--red">Prepare for hot weather rowing</h2>
        <p>If you are going to row in hot weather, take some precautions. Just like with cold weather, you must protect yourself against the effects of extreme temperatures.</p>
        <p>Here are some considerations for hot weather rowing:</p>
        <ul class="chevron-list">
          <li>You and everyone rowing with you should wear protective clothing appropriate for the conditions and activity (including sunglasses and hat).</li>
          <li>Use sunblock with a high SPF factor and reapply when needed.</li>
          <li>Drink plenty of water before, during and after exposure to hot weather.</li>
          <li>Address any symptoms of heat stress immediately.</li>
        </ul>
        <button-sequenced
          class="btn-nav--red"
          :loc="25"
          link="sb-25"
        >
          I’ll prepare for hot weather
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-25 -->
    <div
      id="sb-25"
      class="quiz-bg"
      v-if="$store.state.currentLocation >= 25"
    >
      <content-block>
        <h2 class="content-block__header">In the fog</h2>
        <quiz-radio
          :questions="q01"
          @quiz-complete="
                $store.dispatch('showNext', { n: 26, el: '#menu' })
              "
        ></quiz-radio>
      </content-block>
    </div>

    <footer
      class="course-menu"
      id="menu"
      v-if="$store.state.currentLocation >= 26"
    >
      <h3>Course Menu</h3>
      <cac-menu :menuItems="$store.state.menuItems"></cac-menu>
    </footer>
  </section>
</template>

<script>
// @ is an alias to /src
// import HelloWorld from '@/components/HelloWorld.vue'
import CacHeader from '@/components/cac-components/CacHeader.vue';
import CacMenu from '@/components/cac-components/CacMenu.vue';
import BannerFullWidth from '@/components/BannerFullWidth.vue';
import ContentBlock from '@/components/ContentBlock.vue';
import ButtonSequenced from '@/components/ButtonSequenced.vue';
import AccordionIcon from '@/components/AccordionIcon.vue';
import Accordion from '@/components/Accordion.vue';

// Quiz stuff
import QuizRadio from '@/components/QuizRadio.vue';
import QuizCheckbox from '@/components/QuizCheckbox.vue';

export default {
  name: 'nccp',
  components: {
    CacHeader,
    CacMenu,
    BannerFullWidth,
    ContentBlock,
    ButtonSequenced,
    AccordionIcon,
    Accordion,
    QuizRadio
  },
  data() {
    return {
      showModal: false,
      // 3-01-03
      q01: [
        // Q1
        {
          id: 0,
          questionText: `
            <p class="text-key-take">You’re rowing your single training for an upcoming race. You still have one more piece to do, when you notice fog rolling in and a quick temperature drop. As the fog thickens, you estimate you are 2 km from the dock.</p>
            <p class="text-key-take">What should you do?</p>
          `,
          questionArray: [
            'Continue your piece, but row close to the shore and head to the nearest safe haven that you assessed before going out.',
            'Row back to the club now. Stay close to the shore and use your whistle.',
            'Call the club to request they send a safety boat to guide you in.'
          ],
          corrective: 'Row back to the club now. Stay close to the shore and use your whistle.',
          model: [],
          answer: '',
          answerText: `
            <p>Your last piece can wait. You should row back to the club immediately, staying close to the shore. Blow your whistle to let others know of your presence if you can see no more than 200 metres ahead.</p>
          `,
        },
      ],
    };
  },
  methods: {
    tabContent(index) {
      this.tabIndex = index;
    },
  },
};
</script>

<style lang="scss" scoped>
/*------------------------------------*\
    #PRIMARY COLOURS
  \*------------------------------------*/

/*------------------------------------*\
    #Mixins
  \*------------------------------------*/

/*------------------------------------
    #MEDIA SCREENS < 768px (#MOBILE FIRST)
  \*------------------------------------*/
.banner {
  background-image: url("../assets/img/_chapter-headers/mbl/4-environment-weather-mbl.jpg");
  // background-image: url('../assets/img/_chapter-headers/mbl/01-welcome-mbl.png');
  color: #000;
  background-position: center;
  padding: 60% 10%;
  &__header {
    color: #000;
  }
}

/*------------------------------------*\
    #MEDIA SCREENS > 576px (TABLETS)
  \*------------------------------------*/
@media (min-width: 576px) {
  .banner {
    // background-image: url('../assets/img/_chapter-headers/dsk/01-welcome-dsk.png');
    background-image: url("../assets/img/_chapter-headers/dsk/4-environment-weather-dsk.jpg");
    color: #000;
    padding: 10% 10% 14%;
    background-position: center;
    &__header {
      color: #000;
    }
  }
}

/*------------------------------------*\
  #MEDIA SCREENS > 767px (TABLETS)
\*------------------------------------*/
@media (min-width: 767px) {
  // .banner {
  //   background-image: url('../assets/img/_bg/1-01-01-bg-dsk.png');
  //   color: #fff;
  //   padding: 10% 10%;
  //   background-position: center;
  //   &__header {
  //       color: #fff;
  //   }
  // }
}

/*------------------------------------*\
    #MEDIA SCREENS > 992px
    (LAPTOP, TABLET)
  \*------------------------------------*/
@media (min-width: 992px) {
}

/*------------------------------------*\
    #MEDIA SCREENS > 1200px (DESKTOP)
  \*------------------------------------*/
@media (min-width: 1200px) {
}

/*------------------------------------*\
    #MEDIA SCREENS > 1320px (DESKTOP)
  \*------------------------------------*/
@media (min-width: 1320px) {
}

/*------------------------------------*\
    #MEDIA SCREENS > 1600px (DESKTOP)
  \*------------------------------------*/
@media (min-width: 1600px) {
}
</style>
